// components/island/useVerpassteAnrufe.ts
// Zählt die heute verpassten, nicht abgelösten Anrufe für Badge und Telefon-Widget in
// WidgetsPage.tsx und liefert den jüngsten davon (Name/Zeit im Widget). Bewusst ein schlanker Hook statt eines eigenen Context nach dem
// Vorbild von ChatContext/ZeitContext: die Zahl wird nur an dieser einen Stelle
// gebraucht, ein globaler Bestand wäre hier unnötiger Aufwand. Lädt einmal beim
// Mount und hört danach auf Realtime — dasselbe Muster wie useAnrufe.ts (eigener
// Kanal auf der `anrufe`-Tabelle, neu ausgezählt statt Zeile für Zeile fortgeschrieben:
// bei dem Volumen an Anrufen ist ein schlichtes Neuladen je Ereignis unproblematisch
// und robuster als eine eigene Zu-/Abzähl-Logik für jeden Statuswechsel).
import { useEffect, useState } from 'react';
import { supabase, isDemoMode } from '../../lib/supabase/client';
import type { AnrufZeile } from '../../lib/telefonie/anrufKarte';

function heuteStartISO(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

export interface VerpassteAnrufe {
  anzahl: number;
  /** jüngster verpasster Anruf von heute */
  letzter: AnrufZeile | null;
}

export function useVerpassteAnrufe(): VerpassteAnrufe {
  const [stand, setStand] = useState<VerpassteAnrufe>({ anzahl: 0, letzter: null });

  useEffect(() => {
    if (isDemoMode()) return;
    let abgebrochen = false;

    async function laden() {
      const { data, count } = await supabase
        .from('anrufe')
        .select('*', { count: 'exact' })
        .eq('status', 'verpasst')
        .is('abgeloest_von', null)
        .gte('begonnen_am', heuteStartISO())
        .order('begonnen_am', { ascending: false })
        .limit(1);
      if (!abgebrochen) setStand({ anzahl: count ?? 0, letzter: ((data as AnrufZeile[] | null) ?? [])[0] ?? null });
    }
    void laden();

    const kanal = supabase
      .channel('anrufe-verpasst-badge')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'anrufe' }, () => void laden())
      .subscribe();

    return () => {
      abgebrochen = true;
      void supabase.removeChannel(kanal);
    };
  }, []);

  return stand;
}
